/**
 * @file movement-note-text.ts
 * @description Перевод служебного комментария движения склада на русский
 * (phase19.31, зона «Склад»; дефект приёмки: в колонке «Основание» сырой
 * английский текст сервера — «GoodsReceipt RC…», «Order cancelled: …»).
 *
 * Сервер пишет в `StockMovement.notes` служебные строки. Известные шаблоны
 * (сверено с `apps/api/src/wms/**` и `apps/api/src/orders/status-engine`)
 * переводим реестром «шаблон → текст»; номера документов — в коротком виде
 * («RCP-…018»), полный текст остаётся в подсказке у вызывающего. Неизвестная
 * строка показывается как есть — лучше честный сырой текст, чем пустота.
 *
 * Новый шаблон сервера = одна запись в `NOTE_RULES`.
 */

import { shortDocNumber } from "../short-number";
import { WRITEOFF_REASON_LABEL, type WriteoffReason } from "../../../lib/api-wms";
import { movementNoteText, type MovementLike } from "../../../lib/movements-texts";

/** Правило перевода: шаблон и текст из групп совпадения. */
interface NoteRule {
  pattern: RegExp;
  text: (match: RegExpExecArray) => string;
}

/** Номер документа из группы коротко; пустая группа — пустая строка. */
const num = (value: string | undefined): string => shortDocNumber(value ?? "");

/** Подпись причины списания; неизвестный код — как есть. */
function writeoffReason(code: string | undefined): string {
  const label = WRITEOFF_REASON_LABEL[code as WriteoffReason];
  return label ?? code ?? "";
}

/** Реестр шаблонов — порядок важен только для пересекающихся (их нет). */
const NOTE_RULES: NoteRule[] = [
  // Проведение документов.
  { pattern: /^GoodsReceipt (\S+)$/, text: (m) => `Приёмка ${num(m[1])}` },
  { pattern: /^StockTransfer (\S+)$/, text: (m) => `Перемещение ${num(m[1])}` },
  { pattern: /^Writeoff (\S+) \((\w+)\)$/, text: (m) => `Списание ${num(m[1])} · ${writeoffReason(m[2])}` },
  { pattern: /^Размещение по приёмке (\S+)$/, text: (m) => `Размещение по приёмке ${num(m[1])}` },
  // Отмена заказа: причину пишет человек — оставляем её как есть.
  { pattern: /^Order cancelled: (.+)$/s, text: (m) => `Отмена заказа: ${m[1] ?? ""}` },
  { pattern: /^(Order cancelled|order_cancelled)$/, text: () => "Отмена заказа" },
  // Резервы.
  { pattern: /^Reserve TTL (\d+)\s*min$/, text: (m) => `Резерв на ${m[1]} мин` },
  { pattern: /^TTL expired$/, text: () => "Срок резерва истёк" },
  { pattern: /^Manual release$/, text: () => "Резерв снят вручную" },
  { pattern: /^(Rollback: reserve failed|reserve_rollback)$/, text: () => "Откат резерва: не удалось зарезервировать" },
  { pattern: /^Fulfill reservation → SALE$/, text: () => "Продажа из резерва" },
  { pattern: /^PACKED — Sale from reservation$/, text: () => "Заказ упакован — продажа из резерва" },
  { pattern: /^Compensation: SALE failed after UNRESERVE$/, text: () => "Возврат резерва: продажа не прошла" },
  // Заказ и сборка.
  { pattern: /^order_items_edit$/, text: () => "Изменён состав заказа" },
  { pattern: /^order_items_edit_failed$/, text: () => "Откат изменения состава заказа" },
  { pattern: /^(checkout_)?order_create_failed$/, text: () => "Заказ не оформлен — резерв снят" },
  { pattern: /^picking_complete_rollback:(\S+)$/, text: (m) => `Откат завершения сборки ${num(m[1])}` },
  { pattern: /^picking_lot_changed:(\S+)$/, text: (m) => `Замена партии при сборке ${num(m[1])}` },
  { pattern: /^picking_rebuild:(\S+)$/, text: (m) => `Пересборка задания ${num(m[1])}` },
];

/** Перевод одной строки комментария; неизвестное — как есть. */
export function translateMovementNote(raw: string): string {
  const note = raw.trim();
  for (const rule of NOTE_RULES) {
    const match = rule.pattern.exec(note);
    if (match) return rule.text(match);
  }
  return note;
}

/**
 * Пояснение движения для колонки «Основание»: `text` — по-русски и коротко,
 * `full` — исходная строка для подсказки; `null` — пояснения нет.
 */
export function movementNoteView(movement: MovementLike): { text: string; full: string } | null {
  // Технический перенос на партию подписывает общий реестр (`movements-texts`).
  const base = movementNoteText(movement);
  if (!base) return null;
  const raw = movement.notes?.trim() ?? base;
  return { text: base === raw ? translateMovementNote(raw) : base, full: raw };
}
